import { useMemo } from 'react'
import { X } from 'lucide-react'

import { computeTiming, computeTotalDistanceMeters } from '@fit-editor/fit-core'
import type { ActivityFileId } from '@fit-editor/fit-core'

import { AppVersion } from '@/components/layout/app-version'
import { useCurrentActivity } from '@/hooks/use-current-activity'
import { useUnits } from '@/hooks/use-units'
import { formatDistanceMeters, formatDurationSeconds } from '@/lib/format'
import { getSportLabel } from '@/lib/activity-helpers'
import {
  selectSelectedActivity,
  useActivityStore,
  type LoadedActivity,
} from '@/store/activity-store'
import { cn } from '@/lib/utils'

/**
 * Left sidebar: the list of loaded activities, plus (for whichever one is
 * selected) its file info and any decode errors/warnings — surfaced here
 * explicitly rather than silently swallowed, per the import requirement
 * that a broken file never be added to the list without comment.
 *
 * Each row also has a checkbox (M7) for batch edit/export, deliberately
 * independent of the row's own click-to-select behavior -- ticking boxes
 * for a batch operation never changes which single activity is shown in
 * the dashboard/map/timeline. The toolbar's "Batch" button (see
 * `BatchButton`) only appears once at least one box is ticked.
 *
 * Laid out beside the workspace from `md` up; on narrower viewports the
 * same component is shown in the slide-in `SidebarDrawer` instead (see
 * `AppShell`), which passes its own `className` and closes itself via
 * `onActivitySelected`.
 *
 * Ends with the app version, pinned to the bottom while the list scrolls;
 * the drawer shows it in its own footer instead (`showVersion={false}`).
 */
export function Sidebar({
  className,
  onActivitySelected,
  showVersion = true,
}: {
  className?: string
  /** Called after a row is clicked to select it (not on batch ticks or removal). */
  onActivitySelected?: () => void
  showVersion?: boolean
} = {}) {
  const activities = useActivityStore((s) => s.activities)
  const selectedId = useActivityStore((s) => s.selectedActivityId)
  const selectActivity = useActivityStore((s) => s.selectActivity)
  const removeActivity = useActivityStore((s) => s.removeActivity)
  const batchSelectedIds = useActivityStore((s) => s.batchSelectedIds)
  const toggleBatchSelected = useActivityStore((s) => s.toggleBatchSelected)
  const selected = useActivityStore(selectSelectedActivity)

  return (
    <aside
      className={cn(
        'border-border bg-card flex h-full w-72 min-w-0 shrink-0 flex-col overflow-x-hidden overflow-y-auto border-r',
        className,
      )}
    >
      <div className="border-border flex items-center justify-between border-b px-3 py-2">
        <h2 className="text-card-foreground text-sm font-semibold">Activities</h2>
        {activities.length > 1 && (
          <span className="text-muted-foreground text-xs">
            {batchSelectedIds.length > 0
              ? `${batchSelectedIds.length} selected for batch`
              : 'Tick to batch edit/export'}
          </span>
        )}
      </div>

      {activities.length === 0 ? (
        <p className="text-muted-foreground p-3 text-sm">
          No files loaded yet. Use &quot;Open file(s)&quot; or drop a FIT file onto the workspace.
        </p>
      ) : (
        <ul aria-label="Loaded activities" className="divide-border divide-y">
          {activities.map((loaded) => (
            <SidebarActivityItem
              key={loaded.id}
              loaded={loaded}
              selected={loaded.id === selectedId}
              batchChecked={batchSelectedIds.includes(loaded.id)}
              onSelect={() => {
                selectActivity(loaded.id)
                onActivitySelected?.()
              }}
              onToggleBatch={() => toggleBatchSelected(loaded.id)}
              onRemove={() => removeActivity(loaded.id)}
            />
          ))}
        </ul>
      )}

      {selected != null && <SidebarFileDetails key={selected.id} loaded={selected} />}

      {showVersion && (
        <AppVersion className="border-border bg-card sticky bottom-0 mt-auto border-t px-3 py-2" />
      )}
    </aside>
  )
}

function SidebarActivityItem({
  loaded,
  selected,
  batchChecked,
  onSelect,
  onToggleBatch,
  onRemove,
}: {
  loaded: LoadedActivity
  selected: boolean
  batchChecked: boolean
  onSelect: () => void
  onToggleBatch: () => void
  onRemove: () => void
}) {
  const { result } = useCurrentActivity(loaded.history, { keepPreviousWhileLoading: true })
  const units = useUnits()
  const activity = result?.activity
  const timing = useMemo(() => (activity != null ? computeTiming(activity) : null), [activity])
  const distance = useMemo(
    () => (activity != null ? computeTotalDistanceMeters(activity.records) : null),
    [activity],
  )
  const hasDecodeErrors = activity != null && activity.decodeErrors.length > 0
  const sport = activity != null ? getSportLabel(activity) : undefined

  const summaryParts =
    activity != null && timing != null
      ? [sport, formatDurationSeconds(timing.elapsedTimeSec)]
      : ['Loading…']
  if (distance?.value != null) summaryParts.push(formatDistanceMeters(distance.value, units))

  return (
    <li className="flex items-stretch">
      <label className="flex items-center pl-3 max-md:pr-1">
        <input
          type="checkbox"
          aria-label={`Select ${loaded.fileName} for batch edit/export`}
          checked={batchChecked}
          onChange={onToggleBatch}
          className="accent-primary h-3.5 w-3.5 max-md:h-5 max-md:w-5"
        />
      </label>
      <button
        type="button"
        onClick={onSelect}
        aria-pressed={selected}
        className={cn(
          'hover:bg-accent hover:text-accent-foreground flex min-w-0 flex-1 flex-col gap-0.5 px-3 py-2 text-left text-sm transition-colors',
          selected && 'bg-accent text-accent-foreground',
        )}
      >
        <span className="text-foreground flex min-w-0 items-start gap-1.5 font-medium">
          {hasDecodeErrors && (
            <span
              className="bg-destructive mt-1.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full"
              title="This file has decode errors"
              aria-hidden="true"
            />
          )}
          <span className="min-w-0 [overflow-wrap:anywhere]">{loaded.fileName}</span>
        </span>
        <span className="text-muted-foreground text-xs [overflow-wrap:anywhere]">
          {summaryParts.join(' · ')}
        </span>
      </button>
      <button
        type="button"
        aria-label={`Remove ${loaded.fileName}`}
        onClick={onRemove}
        className="text-muted-foreground hover:bg-background hover:text-foreground focus-visible:ring-ring shrink-0 px-2 outline-none focus-visible:ring-2 max-md:px-3.5"
      >
        <X className="h-3.5 w-3.5 max-md:h-4 max-md:w-4" aria-hidden="true" />
      </button>
    </li>
  )
}

function SidebarFileDetails({ loaded }: { loaded: LoadedActivity }) {
  const { result } = useCurrentActivity(loaded.history, { keepPreviousWhileLoading: true })
  const activity = result?.activity
  const fileId: ActivityFileId | undefined = activity?.fileId
  const decodeErrors = activity?.decodeErrors ?? []

  return (
    <div className="border-border flex flex-col gap-2 border-t p-3 text-xs">
      <h3 className="text-card-foreground text-sm font-semibold [overflow-wrap:anywhere]">
        {loaded.fileName}
      </h3>

      {activity == null ? (
        <p className="text-muted-foreground">Loading file details…</p>
      ) : (
        <>
          <dl className="text-muted-foreground grid grid-cols-2 gap-1">
            <dt>Manufacturer</dt>
            <dd className="text-foreground text-right">
              {fileId?.manufacturer != null ? String(fileId.manufacturer) : 'Unknown'}
            </dd>
            <dt>Product</dt>
            <dd className="text-foreground text-right">
              {fileId?.product != null ? String(fileId.product) : 'Unknown'}
            </dd>
            <dt>Serial number</dt>
            <dd className="text-foreground text-right">
              {fileId?.serialNumber != null ? String(fileId.serialNumber) : 'Unknown'}
            </dd>
          </dl>

          {decodeErrors.length > 0 ? (
            <div
              role="alert"
              className="border-destructive/50 bg-destructive/10 rounded-md border p-2"
            >
              <p className="text-destructive font-medium">
                {decodeErrors.length === 1
                  ? '1 decode issue'
                  : `${decodeErrors.length} decode issues`}
              </p>
              <ul className="text-destructive mt-1 list-inside list-disc [overflow-wrap:anywhere]">
                {decodeErrors.map((error, index) => (
                  <li key={index}>{error.message}</li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="text-muted-foreground">No decode errors.</p>
          )}
        </>
      )}
    </div>
  )
}
